import { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import DashboardShell from "@/components/DashboardShell";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, Layers, ExternalLink, Pencil, Trash2, Loader2 } from "lucide-react";

const empty = { title: "", description: "", visibility: "public", timeline_ids: [] };

export default function Collections() {
  const { user } = useAuth();
  const [cols, setCols] = useState(null);
  const [myTimelines, setMyTimelines] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [c, t] = await Promise.all([api.get("/collections"), api.get("/timelines")]);
      setCols(c.data); setMyTimelines(t.data);
    } catch (e) { toast.error(apiErr(e)); setCols([]); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ title: c.title, description: c.description, visibility: c.visibility, timeline_ids: c.timeline_ids || [] }); setOpen(true); };

  const toggle = (id) => setForm((f) => ({ ...f, timeline_ids: f.timeline_ids.includes(id) ? f.timeline_ids.filter((x) => x !== id) : [...f.timeline_ids, id] }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Give your collection a title"); return; }
    setBusy(true);
    try {
      if (editing) { await api.put(`/collections/${editing.id}`, form); toast.success("Collection updated"); }
      else { await api.post("/collections", form); toast.success("Collection created"); }
      setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/collections/${id}`); setCols((p) => p.filter((c) => c.id !== id)); toast.success("Deleted"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <DashboardShell>
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground">Collections</h1>
          <p className="mt-2 text-muted-foreground">Group related timelines into a themed set with its own public page.</p>
        </div>
        <Button onClick={openNew} className="rounded-full" data-testid="new-collection-btn"><Plus size={16} className="mr-1" /> New collection</Button>
      </div>

      {cols === null ? null : cols.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-16 text-center">
          <div className="mx-auto mb-4 h-14 w-14 rounded-2xl bg-accent grid place-items-center text-primary"><Layers size={26} /></div>
          <h3 className="font-display text-xl font-semibold text-foreground">No collections yet</h3>
          <p className="mt-1 text-muted-foreground">Create a collection like “History of Computing” to group timelines together.</p>
          <Button onClick={openNew} className="mt-6 rounded-full" data-testid="empty-collection-btn">Create collection</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="collections-grid">
          {cols.map((c, i) => (
            <motion.div key={c.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              className="group rounded-2xl border border-border bg-card overflow-hidden transition-transform duration-300 hover:-translate-y-1">
              <div className="h-28 relative overflow-hidden bg-primary grain">
                <div className="absolute inset-0 grid place-items-center text-primary-foreground"><Layers size={30} /></div>
                <span className="absolute top-3 left-3 text-[11px] px-2 py-0.5 rounded-full glass font-medium capitalize">{c.visibility}</span>
              </div>
              <div className="p-5">
                <h3 className="font-display font-semibold text-foreground">{c.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{c.description || "No description"}</p>
                <div className="mt-3 text-xs text-muted-foreground">{c.timeline_ids?.length || 0} timelines</div>
                <div className="mt-4 flex items-center gap-2">
                  <a href={`/c/${user?.username}/${c.slug}`} target="_blank" rel="noreferrer" className="flex-1"><Button variant="outline" size="sm" className="w-full rounded-full" data-testid={`view-collection-${c.slug}`}><ExternalLink size={14} className="mr-1" /> View</Button></a>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-collection-${c.id}`}><Pencil size={15} /></Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="text-destructive" data-testid={`delete-collection-${c.id}`}><Trash2 size={15} /></Button></AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Delete “{c.title}”?</AlertDialogTitle><AlertDialogDescription>This removes the collection. Your timelines are not affected.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(c.id)} className="bg-destructive text-destructive-foreground" data-testid={`confirm-delete-collection-${c.id}`}>Delete</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="collection-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing ? "Edit collection" : "New collection"}</DialogTitle>
            <DialogDescription>Curate a set of timelines with a shared theme.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs text-muted-foreground mb-1.5 block">Title</Label><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="collection-title" placeholder="History of Computing" /></div>
            <div><Label className="text-xs text-muted-foreground mb-1.5 block">Description</Label><Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="min-h-[70px]" data-testid="collection-desc" /></div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Timelines ({form.timeline_ids.length} selected)</Label>
              <div className="max-h-48 overflow-y-auto space-y-1.5 rounded-lg border border-border p-2">
                {myTimelines.length === 0 && <p className="text-sm text-muted-foreground p-2">No timelines yet.</p>}
                {myTimelines.map((t) => (
                  <label key={t.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted cursor-pointer" data-testid={`col-tl-${t.id}`}>
                    <Checkbox checked={form.timeline_ids.includes(t.id)} onCheckedChange={() => toggle(t.id)} />
                    <span className="text-sm text-foreground flex-1 truncate">{t.title}</span>
                    <span className="text-[11px] text-muted-foreground">{t.status}</span>
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">Only published, public timelines appear on the collection's public page.</p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={busy} className="rounded-full" data-testid="save-collection-btn">{busy ? <Loader2 className="animate-spin mr-2" size={16} /> : null} {editing ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
